import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { approvePendingUserAccount, deletePendingUserAccount, getPendingUserAccounts } from '../api/adminApi';
import { ApplicationUserPending } from '../apiModels/applicationUserPending';
import { logError, toNullableLocaleDateTimeString } from '../utilities';
import PendingUserTableApproveButton from './PendingUserTableApproveButton';
import PendingUserTableDeleteButton from './PendingUserTableDeleteButton';
import ConfirmModal, { ButtonLoadingSpinner, ElementWithKey } from './ConfirmModal';
import GenericDataTable from './GenericDataTable';

const TableRowFormatter = (props: {account: ApplicationUserPending, setApproveConfirmSelectedAccount: (a: string | null) => void, setDeleteConfirmSelectedAccount: (a: string | null) => void}) => {
    var dateTimeCreated = new Date(props.account.dateCreated);

    return (
        <tr key={props.account.id}>
            <td>{props.account.email}</td>
            <td className='text-center fs-4 p-0'>
                {props.account.emailConfirmed ? 
                    <i className='bi bi-check-square'></i> 
                : ''}
            </td>
            <td>{toNullableLocaleDateTimeString(dateTimeCreated)}</td>
            <td className='text-end'>
                <PendingUserTableApproveButton isEmailConfirmed={props.account.emailConfirmed}
                    onClick={() => props.setApproveConfirmSelectedAccount(props.account.id)} />
                <PendingUserTableDeleteButton onClick={() => props.setDeleteConfirmSelectedAccount(props.account.id)} />
            </td>
        </tr>
    );
};

export default function PendingUserTable() {
    const [ isDataTableLoading, setIsDataTableLoading ] = useState<boolean>(true);
    const [ userAccounts, setUserAccounts ] = useState<ApplicationUserPending[]>([]);
    const notifyUserOfError = (err: string) => toast(err, { type: 'error' });
    const notifyUserOfSuccess = (msg: string) => toast(msg, { type: 'success' });

    const [approveConfirmSelectedAccount, setApproveConfirmSelectedAccount] = useState<string | null>(null);
    const [deleteConfirmSelectedAccount, setDeleteConfirmSelectedAccount] = useState<string | null>(null);
    const [approveConfirmProcessing, setApproveConfirmProcessing] = useState<boolean>(false);
    const [deleteConfirmProcessing, setDeleteConfirmProcessing] = useState<boolean>(false);

    const loadUserAccounts = async() => {
        try {
            let accounts = await getPendingUserAccounts();
            setUserAccounts(accounts);
        } 
        catch (error: any) {
            if (error instanceof Error) {
                notifyUserOfError(error.message);
            }
    
            logError(error);
        }
        finally {
            setIsDataTableLoading(false);
        }
    };
    
    const approveAccount = async() => {
        if (approveConfirmSelectedAccount === null) {
            notifyUserOfError('No account selected for approval.');
        }
        else {
            setApproveConfirmProcessing(true);
    
            try{
                await approvePendingUserAccount(approveConfirmSelectedAccount);
                setApproveConfirmSelectedAccount(null);
                notifyUserOfSuccess('Account request approved.');
                await loadUserAccounts();
            } 
            catch (error: any) {
                if (error instanceof Error) {
                    notifyUserOfError(error.message);
                }
        
                logError(error);
            }
            finally{
                setApproveConfirmProcessing(false);
            }
        }
    };
    
    const deleteAccount = async() => {
        if (deleteConfirmSelectedAccount === null) {
            notifyUserOfError('No account selected for approval.');
        }
        else {
            setDeleteConfirmProcessing(true);
    
            try{
                await deletePendingUserAccount(deleteConfirmSelectedAccount);
                setDeleteConfirmSelectedAccount(null);
                notifyUserOfSuccess('Account request deleted.');
                await loadUserAccounts();
            } 
            catch (error: any) {
                if (error instanceof Error) {
                    notifyUserOfError(error.message);
                }
        
                logError(error);
            }
            finally{
                setDeleteConfirmProcessing(false);
            }
        }
    };
    
    useEffect(() => {
        loadUserAccounts();
    }, []);

    const ApproveConfirmModalButtons: ElementWithKey[] = [
        { 
            key: 0, 
            element: <button 
                className='btn btn-primary' 
                onClick={() => approveAccount()}
                disabled={approveConfirmProcessing}>
                {approveConfirmProcessing ? <ButtonLoadingSpinner /> : 'Approve'}
            </button>
        },
        {
            key: 1,
            element: <button 
                className='btn btn-default' 
                onClick={() => setApproveConfirmSelectedAccount(null)}>
                Cancel
            </button>
        }
    ];

    const DeleteConfirmModalButtons = [
        { 
            key: 0, 
            element: <button 
                className='btn btn-danger' 
                onClick={() => deleteAccount()}
                disabled={deleteConfirmProcessing}>
                {deleteConfirmProcessing ? <ButtonLoadingSpinner /> : 'Delete'}
            </button>
        },
        { 
            key: 1, 
            element: <button className='btn btn-default' onClick={() => setDeleteConfirmSelectedAccount(null)}>
                Cancel
            </button>
        }
    ];

    return (
        <>
            <h3>Pending Administrator Accounts</h3>

            <GenericDataTable data={userAccounts} 
                dataFormatter={data => <>{data.map(d => <TableRowFormatter key={d.id} setApproveConfirmSelectedAccount={setApproveConfirmSelectedAccount} setDeleteConfirmSelectedAccount={setDeleteConfirmSelectedAccount} account={d} />)}</>}
                noDataMessage='There are no pending administrator accounts.'
                columnCount={4}
                isLoading={isDataTableLoading}>
                <tr>
                    <td>Email</td>
                    <td className='text-center'>Email Confirmed</td>
                    <td>Created</td>
                    <td></td>
                </tr>
            </GenericDataTable>
            <ToastContainer />
            <ConfirmModal title='Confirm Approval' 
                body='Are you sure you want to approve this administrator account? This user will be given access to the Revell Logicstics Solutions administrator dashboard.' 
                buttons={ApproveConfirmModalButtons} 
                show={approveConfirmSelectedAccount !== null} 
                setShow={() => setApproveConfirmSelectedAccount(null)} />
            <ConfirmModal title='Confirm Deletion' 
                body='Are you sure you want to delete this pending account? This is irreversible.' 
                buttons={DeleteConfirmModalButtons} 
                show={deleteConfirmSelectedAccount !== null} 
                setShow={() => setDeleteConfirmSelectedAccount(null)} />
        </>
    );
}
